import { Loader2 } from 'lucide-react';

import { Label } from '@apps/web/components/ui/label';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { cn } from '@apps/web/lib/utils';
import {
    type JoinedOutreachRead,
    type Shift,
} from '@libraries/typescript/api_client';

export type PerShiftCarePlan = {
    readonly summary: string;
    readonly bullets: string;
};

// Resolve the text {care_plan_summary} renders for one shift, mirroring the
// worker (`normalize_shift_care_plans` + shift_handled_notify_activities.py):
// blank fields are dropped first, then SMS bullets win over the prose
// summary, and missing plans render blank.
export function resolveCarePlanSummary(
    perShiftCarePlans: Readonly<Record<string, PerShiftCarePlan>> | undefined,
    shiftId: string,
): string {
    const carePlan = perShiftCarePlans?.[shiftId];
    return carePlan?.bullets?.trim() || carePlan?.summary?.trim() || '';
}

// Care plans are per-client, so multiple shifts for the same client share one
// plan. Dedupe by client_id; a shift with no client_id is treated as its own
// unique client so distinct unmatched shifts never merge.
export function dedupeShiftsByClient(shifts: Array<Shift>): Array<Shift> {
    const seenClientIds = new Set<string>();
    return shifts.filter((shift) => {
        if (!shift.client_id) {
            return true;
        }
        if (seenClientIds.has(shift.client_id)) {
            return false;
        }
        seenClientIds.add(shift.client_id);
        return true;
    });
}

// Read care plan content from outreach metadata. Per-shift entries are the
// canonical source; top-level care_plan_summary/care_plan_bullets are a legacy
// fallback only used when no per-shift entry exists for a given shift.
export function readCarePlanFromMetadata(outreach: JoinedOutreachRead): {
    readonly carePlanSummary: string;
    readonly carePlanBullets: string;
    readonly perShiftCarePlans: Record<string, PerShiftCarePlan>;
} {
    const metadata = outreach.outreach_metadata as Record<string, unknown>;
    const rawShiftPlans = metadata['shift_care_plans'];
    const perShiftCarePlans: Record<string, PerShiftCarePlan> = {};
    if (rawShiftPlans && typeof rawShiftPlans === 'object') {
        for (const [shiftId, entry] of Object.entries(
            rawShiftPlans as Record<string, unknown>,
        )) {
            if (entry && typeof entry === 'object') {
                const { summary, bullets } = entry as {
                    readonly summary?: string | null;
                    readonly bullets?: string | null;
                };
                perShiftCarePlans[shiftId] = {
                    summary: summary ?? '',
                    bullets: bullets ?? '',
                };
            }
        }
    }
    const topLevelSummary =
        typeof metadata['care_plan_summary'] === 'string'
            ? (metadata['care_plan_summary'] as string)
            : '';
    const topLevelBullets =
        typeof metadata['care_plan_bullets'] === 'string'
            ? (metadata['care_plan_bullets'] as string)
            : '';
    const singleShiftId =
        outreach.shift_ids.length === 1 ? String(outreach.shift_ids[0]) : null;
    const singleShiftEntry = singleShiftId
        ? perShiftCarePlans[singleShiftId]
        : undefined;
    return {
        carePlanSummary: singleShiftEntry
            ? singleShiftEntry.summary
            : topLevelSummary,
        carePlanBullets: singleShiftEntry
            ? singleShiftEntry.bullets
            : topLevelBullets,
        perShiftCarePlans,
    };
}

type CarePlanSummaryPreviewProps = {
    readonly isLoading?: boolean;
    readonly carePlanSummary?: string;
    readonly carePlanBullets?: string;
    readonly perShiftCarePlans?: Record<string, PerShiftCarePlan>;
    readonly selectedShifts: Array<Shift>;
    readonly timezone?: string;
    readonly showSms?: boolean;
    readonly showVoice?: boolean;
    readonly onCarePlanSummaryChange?: (value: string) => void;
    readonly onCarePlanBulletsChange?: (value: string) => void;
    readonly onPerShiftCarePlanChange?: (
        shiftId: string,
        plan: PerShiftCarePlan,
    ) => void;
};

export function CarePlanSummaryPreview({
    isLoading,
    carePlanSummary,
    carePlanBullets,
    perShiftCarePlans,
    selectedShifts,
    showSms,
    showVoice,
    onCarePlanSummaryChange,
    onCarePlanBulletsChange,
    onPerShiftCarePlanChange,
}: CarePlanSummaryPreviewProps) {
    const { formatName } = useFormatPersonName();
    const isEditable =
        !!onCarePlanSummaryChange ||
        !!onCarePlanBulletsChange ||
        !!onPerShiftCarePlanChange;
    const isMultiShift = selectedShifts.length > 1;
    const hasPerShiftData =
        perShiftCarePlans && Object.keys(perShiftCarePlans).length > 0;

    // When step filtering is provided, use it. Otherwise show
    // whatever content is available.
    const hasStepFilter = showSms !== undefined || showVoice !== undefined;
    const effectiveShowSms = hasStepFilter ? !!showSms : true;
    const effectiveShowVoice = hasStepFilter ? !!showVoice : true;

    const hasBullets = effectiveShowSms && (!!carePlanBullets || isEditable);
    const hasSummary = effectiveShowVoice && (!!carePlanSummary || isEditable);
    const showBoth = hasBullets && hasSummary;

    if (
        !hasBullets &&
        !hasSummary &&
        !hasPerShiftData &&
        !isLoading &&
        !isEditable
    ) {
        return null;
    }

    return (
        <div className="space-y-2">
            <Label>Care Plan Summary</Label>
            {isLoading ? (
                <div className="rounded-lg border p-4 text-sm">
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating care plan summary…
                    </div>
                </div>
            ) : isMultiShift && hasPerShiftData ? (
                <div className="space-y-4">
                    {dedupeShiftsByClient(selectedShifts).map((shift) => {
                        const plan = perShiftCarePlans[shift.id];
                        if (!plan) {
                            return null;
                        }
                        // Care plans are per-client, so edits to the displayed
                        // shift must propagate to every shift for that client.
                        // A shift with no client_id is its own client, so it
                        // only propagates to itself.
                        const clientShiftIds = selectedShifts
                            .filter((other) =>
                                shift.client_id
                                    ? other.client_id === shift.client_id
                                    : other.id === shift.id,
                            )
                            .map((other) => other.id);
                        // In editable mode keep both columns visible so users
                        // can fill in empty fields. In non-editable mode hide
                        // empty columns to avoid bordered empty boxes.
                        const shiftBullets =
                            effectiveShowSms &&
                            (isEditable || !!plan.bullets?.trim());
                        const shiftSummary =
                            effectiveShowVoice &&
                            (isEditable || !!plan.summary?.trim());
                        const shiftShowBoth = shiftBullets && shiftSummary;
                        if (!shiftBullets && !shiftSummary) {
                            return null;
                        }
                        const handleBulletsChange =
                            onPerShiftCarePlanChange &&
                            ((value: string) =>
                                clientShiftIds.forEach((id) =>
                                    onPerShiftCarePlanChange(id, {
                                        summary: plan.summary,
                                        bullets: value,
                                    }),
                                ));
                        const handleSummaryChange =
                            onPerShiftCarePlanChange &&
                            ((value: string) =>
                                clientShiftIds.forEach((id) =>
                                    onPerShiftCarePlanChange(id, {
                                        summary: value,
                                        bullets: plan.bullets,
                                    }),
                                ));
                        return (
                            <div key={shift.id} className="space-y-2">
                                <p className="text-sm font-medium">
                                    {formatName(shift.patient_name)}
                                </p>
                                <CarePlanColumns
                                    bullets={
                                        shiftBullets ? plan.bullets : undefined
                                    }
                                    summary={
                                        shiftSummary ? plan.summary : undefined
                                    }
                                    showBoth={!!shiftShowBoth}
                                    onBulletsChange={handleBulletsChange}
                                    onSummaryChange={handleSummaryChange}
                                />
                            </div>
                        );
                    })}
                </div>
            ) : (
                <CarePlanColumns
                    bullets={hasBullets ? (carePlanBullets ?? '') : undefined}
                    summary={hasSummary ? (carePlanSummary ?? '') : undefined}
                    showBoth={showBoth}
                    onBulletsChange={onCarePlanBulletsChange}
                    onSummaryChange={onCarePlanSummaryChange}
                />
            )}
        </div>
    );
}

function CarePlanColumns({
    bullets,
    summary,
    showBoth,
    onBulletsChange,
    onSummaryChange,
}: {
    readonly bullets?: string;
    readonly summary?: string;
    readonly showBoth: boolean;
    readonly onBulletsChange?: (value: string) => void;
    readonly onSummaryChange?: (value: string) => void;
}) {
    const showBullets = bullets !== undefined;
    const showSummary = summary !== undefined;
    return (
        <div className="grid gap-4 sm:grid-cols-2">
            {showBullets && (
                <div
                    className={cn(
                        'flex flex-col space-y-1',
                        !showBoth && 'sm:col-span-2',
                    )}
                >
                    {showBoth && (
                        <p className="text-muted-foreground text-xs font-medium">
                            SMS
                        </p>
                    )}
                    {onBulletsChange ? (
                        <Textarea
                            value={bullets ?? ''}
                            onChange={(event) =>
                                onBulletsChange(event.target.value)
                            }
                            className="min-h-24 flex-1"
                        />
                    ) : (
                        <div className="flex-1 rounded-lg border p-4 text-sm">
                            <p className="text-muted-foreground whitespace-pre-wrap">
                                {bullets}
                            </p>
                        </div>
                    )}
                </div>
            )}
            {showSummary && (
                <div
                    className={cn(
                        'flex flex-col space-y-1',
                        !showBoth && 'sm:col-span-2',
                    )}
                >
                    {showBoth && (
                        <p className="text-muted-foreground text-xs font-medium">
                            Voice
                        </p>
                    )}
                    {onSummaryChange ? (
                        <Textarea
                            value={summary ?? ''}
                            onChange={(event) =>
                                onSummaryChange(event.target.value)
                            }
                            className="min-h-24 flex-1"
                        />
                    ) : (
                        <div className="flex-1 rounded-lg border p-4 text-sm">
                            <p className="text-muted-foreground whitespace-pre-wrap">
                                {summary}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
